import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import axios from 'axios';

import { AppStateType } from '../root-reducer';

import notification from '../../../services/notification';
import history from '../../../services/history';
import reasonService from '../reason/reason-service';
import userService from '~/services/modules/user/user-service';
import domainService from '../../../services/modules/domain/domain-service';

import clientService from './client-service';
import * as clientActions from './client-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_FILTER_REQUEST,
  SAVE_RATING_REQUEST,
  DELETE_IMAGE,
  SAVE_IMAGES,
  SAVE_CHECKLIST_REQUEST
} from './client-constants';
import { StateListType } from '~/types/state-list-type';
import { CLIENT_LIST, DAYS_OF_WEEK, ISA_ERROR, REASON_TYPE } from '../../../constants';
import { formatMessage } from '~/i18n/translate';

import * as orderActions from '~/store/modules/order/order-create-actions';
import { parse, isValid, format } from 'date-fns';
import { realToNumber } from '~/utils/format';
import { parsedArrayChecklist } from './client-utils';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';

const parseBirthDate = (value?: string | Date | null) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const parsed = parse(trimmed, 'dd/MM/yyyy', new Date());
      return isValid(parsed) ? parsed : null;
    }

    const parsed = new Date(trimmed);
    return isValid(parsed) ? parsed : null;
  }

  return null;
};

const formatBirthDate = (value?: string | Date | null) => {
  if (!value) return '';

  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim())) {
    return value.trim();
  }

  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? format(date, 'dd/MM/yyyy') : '';
};

const INITIAL_VALUES = {
  governmentalCode: '',
  externalCode: '',
  clientName: '',
  name: '',
  stateRegistration: '',
  birthDate: '',
  dayOfVisit: '',
  regulatoryAgency: '',
  regulatoryUF: '',
  regulatoryResponsible: '',

  commercialInfoPostalCode: '',
  commercialInfoAddress: '',
  commercialInfoCity: '',
  commercialInfoDistrict: '',
  commercialInfoNumber: '',
  commercialInfoState: '',
  commercialInfoPhoneNumber: '',
  commercialInfoCellphoneNumber: '',
  commercialInfoCityIBGECode: '',

  deliveryInfoPostalCode: '',
  deliveryInfoAddress: '',
  deliveryInfoCity: '',
  deliveryInfoDistrict: '',
  deliveryInfoNumber: '',
  deliveryInfoState: '',
  deliveryInfoPhoneNumber: '',
  deliveryInfoCityIBGECode: '',

  billingInfoPostalCode: '',
  billingInfoAddress: '',
  billingInfoCity: '',
  billingInfoDistrict: '',
  billingInfoPhoneNumber: '',
  billingInfoState: '',
  billingInfoNumber: '',
  billingInfoCityIBGECode: '',

  potential: 0,
  creditLimit: 0,
  clientGroup: null,
  paymentOption: null,
  paymentPlan: null,
  squareExternalCode: null,
  subsidizedSale: false,
  finalConsumer: null,
  taxpayer: null,
  simpleNational: false,
  customerUsesSourceST: false,
  blocked: false,
  deadlineSuggestion: null,
  creditLimitSuggestion: 0,
  buyerName: '',
  buyerContact: '',
  observation: '',
  observation2: null,
  observation3: null,
  email: '',
  invoiceEmail: '',
  billingEmail: '',
  subsidiaries: [],
  documents: []
};

let cancel;
const CancelToken = axios.CancelToken;

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a?.externalCode - b?.externalCode);
};

export function* getAll({ typeList }: any) {
  const list: StateListType = yield select((state: AppStateType) => state.client.list);
  const fields = [
    'externalCode',
    'lastPurchaseDate',
    'clientName',
    'name',
    'governmentalCode',
    'blocked',
    'createdAt',
    'updatedAt'
  ];

  try {
    yield put(clientActions.setLoadingData(true));
    const { externalCode: userExternalCode } = yield select((state: AppStateType) => state.auth.user);

    const { page, rowsPerPage, queryText } = list;

    if (typeList === CLIENT_LIST.REGISTERED) {
      const { data } = yield call(clientService.getAll, {
        fields,
        skip: page === 0 ? 0 : page * rowsPerPage,
        limit: rowsPerPage ?? 12,
        queryString: queryText,
        userExternalCode
      });

      const { data: rowsCount } = yield call(clientService.getRowsCount, {
        fields,
        skip: page === 0 ? 0 : page * rowsPerPage,
        limit: rowsPerPage ?? 12,
        queryString: queryText,
        userExternalCode
      });

      if (data) {
        yield put(clientActions.getAllSuccess({ data: [...data], rowsCount: rowsCount[0]?.rowsCount }));
      }
    } else {
      const { data } = yield call(clientService.getAllImported, {
        fields,
        skip: page === 0 ? 0 : page * rowsPerPage,
        limit: rowsPerPage ?? 12,
        queryString: queryText,
        userExternalCode
      });

      const { data: rowsCount } = yield call(clientService.getRowsCountAction, {
        fields,
        skip: page === 0 ? 0 : page * rowsPerPage,
        limit: rowsPerPage ?? 12,
        queryString: queryText,
        userExternalCode
      });

      if (data) {
        yield put(clientActions.getAllSuccess({ data: [...data], rowsCount: rowsCount[0]?.rowsCount }));
      }
    }

    yield put(clientActions.setLoadingData(false));
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error?.response?.status === 404) {
        yield call(notification.warning, formatMessage('clientListWalletNotFound'));
      } else {
        yield put({ type: ISA_ERROR, error });
      }
    }

    yield put(clientActions.setLoadingData(false));
  }
}

export function* getFilter(action: any) {
  const { type, queryString, skip, limit, ...visitDaysParams } = action;
  const fields = [
    'externalCode',
    'clientName',
    'name',
    'governmentalCode',
    'blocked',
    'commercialInfo',
    'lastPurchaseDate',
    'nextVisit',
    'openBills',
    'hadSaleThisMonth',
    'hadVendorSaleThisMonth',
    'potentialSalesCustomers',
    'promoterExternalCode'
  ];

  yield delay(200);

  try {
    yield put(clientActions.setLoadingData(true));
    const { externalCode: userExternalCode } = yield select((state: AppStateType) => state.auth.user);
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const list = yield select((state: AppStateType) => state.client?.list);

    if (cancel !== undefined) cancel();

    const params = {
      engine: configuration?.searchEngine?.client
        ?.map(engine => {
          if (engine.complement.length > 0) {
            return engine.complement.map(complementField => {
              return `${engine.field}.${complementField}`;
            });
          }
          return engine.field;
        })
        .flat()
        .join(','),
      skip: skip === 0 ? 0 : skip,
      limit: limit ?? 10 /* 10 */
    };

    const { data } = yield call(
      clientService.getQuery,
      {
        fields,
        skip,
        limit,
        queryString,
        userExternalCode,
        showRatting: true,
        ...params,
        ...visitDaysParams
      },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        })
      }
    );

    if (queryString) {
      yield put(
        clientActions.getFilterSuccess({
          clients: skip === 0 ? [...data] : [...(data ?? []), ...list?.rows],
          hasMore: data?.length > 0 && data?.length >= limit ? true : false,
          skip,
          limit
        })
      );

      return;
    }

    yield put(
      clientActions.getFilterSuccess({
        clients: skip === 0 ? [...data] : [...(data ?? []), ...list?.rows],
        hasMore: data?.length > 0 && data?.length >= limit ? true : false,
        skip,
        limit
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingData(false));
  }
}

export function* getById({ id, typeList }: any) {
  try {
    yield put(clientActions.setLoadingData(true));
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const [
      { data: clientGroups },
      { data: activities },
      { data: paymentOptions },
      { data: paymentPlans },
      { data: squares },
      { data: reasons },
      { data: users },
      { data: subsidiaries }
    ] = yield all([
      call(domainService.getClientGroups),
      call(domainService.getActivities),
      call(domainService.getPaymentOptions),
      call(domainService.getPaymentPlans),
      call(domainService.getSquares),
      call(reasonService.getAll),
      call(userService.getUsers),
      call(domainService.getSubsidiaries, {})
    ]);

    const selects = {
      clientGroups: sort(clientGroups),
      activities: sort(activities),
      paymentOptions: sort(paymentOptions),
      paymentPlans: sort(paymentPlans),
      squares: sort(squares),
      reasons: reasons.filter(x => x.type === REASON_TYPE.CLIENTE_BLOCK),
      users: sort(users)?.map(({ name, externalCode }) => ({ externalCode, name })),
      daysOfWeek: DAYS_OF_WEEK,
      subsidiaries: sort(subsidiaries ?? [])
    };

    if (!id) {
      yield put(
        clientActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES
          },
          selects
        })
      );
    } else {
      if (typeList === CLIENT_LIST.IMPORTED) {
        const { data } = yield call(clientService.getClientImported, id);
        const { data: ratings } = yield call(clientService.getAllRatings, {
          clientExternalCode: data?.externalCode
        });

        if (data) {
          yield put(
            clientActions.getByIdSuccess({
              data: {
                ...data,
                clientGroup: selects?.clientGroups?.find(c => c.externalCode === data?.clientGroupExternalCode),
                paymentOption: selects?.paymentOptions?.find(c => c.externalCode === data?.paymentOptionExternalCode),
                paymentPlan: selects?.paymentPlans?.find(c => c.externalCode === data?.paymentPlanExternalCode),
                squareExternalCode: selects?.squares?.find(c => c.externalCode === data?.squareExternalCode) ?? null,
                subsidiaries: (data?.subsidiaries ?? [])
                  .map(code => selects?.subsidiaries?.find(s => String(s?.externalCode) === String(code)))
                  .filter(Boolean),
                blockReason: selects?.reasons?.find(x => x.externalCode === data?.blockReason),
                ratings,
                dayOfVisit: selects?.daysOfWeek?.find(x => x?.value === data?.dayOfVisit),

                commercialInfoPostalCode: data?.commercialInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                commercialInfoAddress: data?.commercialInfo?.address,
                commercialInfoCity: data?.commercialInfo?.city,
                commercialInfoDistrict: data?.commercialInfo?.district,
                commercialInfoNumber: data?.commercialInfo?.number,
                commercialInfoState: data?.commercialInfo?.state,
                commercialInfoPhoneNumber: data?.commercialInfo?.phoneNumber?.replace(
                  /[&/\\#,+()$~%.'" ():*?<>{}-]/g,
                  ''
                ),
                commercialInfoCellphoneNumber: data?.commercialInfo?.cellphoneNumber,
                commercialInfoCityIBGECode: data?.commercialInfo?.cityIBGECode,

                deliveryInfoPostalCode: data?.deliveryInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                deliveryInfoAddress: data?.deliveryInfo?.address,
                deliveryInfoCity: data?.deliveryInfo?.city,
                deliveryInfoDistrict: data?.deliveryInfo?.district,
                deliveryInfoNumber: data?.deliveryInfo?.number,
                deliveryInfoState: data?.deliveryInfo?.state,
                deliveryInfoPhoneNumber: data?.deliveryInfo?.phoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                deliveryInfoCityIBGECode: data?.deliveryInfo?.cityIBGECode,

                billingInfoPostalCode: data?.billingInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                billingInfoAddress: data?.billingInfo?.address,
                billingInfoCity: data?.billingInfo?.city,
                billingInfoDistrict: data?.billingInfo?.district,
                billingInfoPhoneNumber: data?.billingInfo?.phoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                billingInfoState: data?.billingInfo?.state,
                billingInfoNumber: data?.billingInfo?.number,
                billingInfoCityIBGECode: data?.billingInfo?.cityIBGECode,
                birthDate: formatBirthDate(data?.birthDate)
              },
              selects
            })
          );
        }
      } else {
        const { data } = yield call(clientService.getClient, id);

        const [
          { data: ratings },
          { data: images },
          { data: coverImage },
          { data: questionary },
          { data: historicQuestionary }
        ] = yield all([
          call(clientService.getAllRatings, {
            clientExternalCode: data?.externalCode
          }),
          call(clientService.getAllImages, {
            clientExternalCode: data?.externalCode
          }),
          call(clientService.getCoverImage, {
            clientExternalCode: data?.externalCode
          }),
          call(clientService.getQuestionary),
          call(clientService.getHistoricQuestionary, {
            clientExternalCode: data?.externalCode
          })
        ]);

        const parsedImages =
          images?.length > 0
            ? images?.map(image => ({
                imageUrl: image,
                coverImageUrl: coverImage === image
              }))
            : [];

        if (data) {
          yield put(
            clientActions.getByIdSuccess({
              data: {
                ...data,
                clientGroup: selects?.clientGroups?.find(c => c.externalCode === data?.clientGroupExternalCode),
                paymentOption: selects?.paymentOptions?.find(c => c.externalCode === data?.paymentOptionExternalCode),
                paymentPlan: selects?.paymentPlans?.find(c => c.externalCode === data?.paymentPlanExternalCode),
                squareExternalCode: selects?.squares?.find(c => c.externalCode === data?.squareExternalCode) ?? null,
                subsidiaries: (data?.subsidiaries ?? [])
                  .map(code => selects?.subsidiaries?.find(s => String(s?.externalCode) === String(code)))
                  .filter(Boolean),
                blockReason: selects?.reasons?.find(x => x.externalCode === data?.blockReason),
                ratings,
                questionary,
                historicQuestionary,
                images: parsedImages,
                dayOfVisit: selects?.daysOfWeek?.find(x => x?.value === data?.dayOfVisit),
                commercialInfoPostalCode: data?.commercialInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                commercialInfoAddress: data?.commercialInfo?.address,
                commercialInfoCity: data?.commercialInfo?.city,
                commercialInfoDistrict: data?.commercialInfo?.district,
                commercialInfoNumber: data?.commercialInfo?.number,
                commercialInfoState: data?.commercialInfo?.state,
                commercialInfoPhoneNumber: data?.commercialInfo?.phoneNumber?.replace(
                  /[&/\\#,+()$~%.'" ():*?<>{}-]/g,
                  ''
                ),
                commercialInfoCellphoneNumber: data?.commercialInfo?.cellphoneNumber,
                commercialInfoCityIBGECode: data?.commercialInfo?.cityIBGECode,

                deliveryInfoPostalCode: data?.deliveryInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                deliveryInfoAddress: data?.deliveryInfo?.address,
                deliveryInfoCity: data?.deliveryInfo?.city,
                deliveryInfoDistrict: data?.deliveryInfo?.district,
                deliveryInfoNumber: data?.deliveryInfo?.number,
                deliveryInfoState: data?.deliveryInfo?.state,
                deliveryInfoPhoneNumber: data?.deliveryInfo?.phoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                deliveryInfoCityIBGECode: data?.deliveryInfo?.cityIBGECode,

                billingInfoPostalCode: data?.billingInfo?.postalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                billingInfoAddress: data?.billingInfo?.address,
                billingInfoCity: data?.billingInfo?.city,
                billingInfoDistrict: data?.billingInfo?.district,
                billingInfoPhoneNumber: data?.billingInfo?.phoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
                billingInfoState: data?.billingInfo?.state,
                billingInfoNumber: data?.billingInfo?.number,
                billingInfoCityIBGECode: data?.billingInfo?.cityIBGECode,
                birthDate: formatBirthDate(data?.birthDate)
              },
              selects
            })
          );
        }
      }
    }
    yield put(clientActions.setLoadingData(false));
  } catch (error) {
    yield put(clientActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload, files } = data;
    yield put(clientActions.setLoadingSend(true));

    const client = {
      ...payload,

      regulatoryAgency: payload?.regulatoryAgency,
      regulatoryUF: payload?.regulatoryUF,
      regulatoryResponsible: payload?.regulatoryResponsible,
      clientGroupExternalCode: payload?.clientGroup?.externalCode,
      activityExternalCodes: payload?.activityExternalCodes
        ? payload.activityExternalCodes
            .filter(a => a !== null)
            .map(a => (typeof a === 'object' ? String(a.externalCode) : String(a)))
        : [],
      paymentOptionExternalCode: payload?.paymentOption?.externalCode,
      paymentPlanExternalCode: payload?.paymentPlan?.externalCode,
      squareExternalCode: payload?.squareExternalCode?.externalCode ?? null,
      blockedDate: payload?.blocked ? new Date() : null,
      commercialInfo: {
        address: payload?.commercialInfoAddress,
        city: payload?.commercialInfoCity,
        district: payload?.commercialInfoDistrict,
        state: payload?.commercialInfoState,
        cellphoneNumber: payload?.commercialInfoCellphoneNumber,
        number: payload?.commercialInfoNumber,
        cityIBGECode: payload?.commercialInfoCityIBGECode,
        phoneNumber: payload?.commercialInfoPhoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}-]/g, ''),
        postalCode: payload?.commercialInfoPostalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, '')
      },

      deliveryInfo: {
        address: payload?.deliveryInfoAddress,
        number: payload?.deliveryInfoNumber,
        district: payload?.deliveryInfoDistrict,
        phoneNumber: payload?.deliveryInfoPhoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
        state: payload?.deliveryInfoState,
        postalCode: payload?.deliveryInfoPostalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
        city: payload?.deliveryInfoCity,
        cityIBGECode: payload?.billingInfoCityIBGECode
      },

      billingInfo: {
        address: payload?.billingInfoAddress,
        number: payload?.billingInfoNumber,
        district: payload?.billingInfoDistrict,
        phoneNumber: payload?.billingInfoPhoneNumber?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
        state: payload?.billingInfoState,
        postalCode: payload?.billingInfoPostalCode?.replace(/[&/\\#,+()$~%.'" ():*?<>{}]/g, ''),
        city: payload?.billingInfoCity,
        cityIBGECode: payload?.billingInfoCityIBGECode
      },

      deadlineSuggestion: payload?.deadlineSuggestion?.externalCode,
      creditLimitSuggestion: realToNumber(payload?.creditLimitSuggestion),
      customerUsesSourceST: payload?.customerUsesSourceST ?? false,
      observation2: payload?.observation2 || null,
      observation3: payload?.observation3 || null,
      dayOfVisit: String(payload?.dayOfVisit?.value) ?? null,
      subsidiaries: (payload?.subsidiaries ?? [])
        .filter(s => s !== null && s !== undefined)
        .map(s => (typeof s === 'object' ? String(s.externalCode) : String(s))),
      minimumBatchExpiryDate: payload?.minimumBatchExpiryDate === null ? null : Number(payload.minimumBatchExpiryDate),
      birthDate: parseBirthDate(payload?.birthDate)
    };

    //copia o objeto client, e retira o status
    const clientParsed = (({
      status,
      commercialInfoAddress,
      commercialInfoCity,
      commercialInfoDistrict,
      commercialInfoState,
      commercialInfoCellphoneNumber,
      commercialInfoNumber,
      commercialInfoCityIBGECode,
      commercialInfoPhoneNumber,
      commercialInfoPostalCode,

      deliveryInfoNumber,
      deliveryInfoAddress,
      deliveryInfoDistrict,
      deliveryInfoPhoneNumber,
      deliveryInfoState,
      deliveryInfoPostalCode,
      deliveryInfoCity,

      billingInfoCityIBGECode,
      billingInfoAddress,
      billingInfoNumber,
      billingInfoDistrict,
      billingInfoPhoneNumber,
      billingInfoState,
      billingInfoPostalCode,
      billingInfoCity,

      ...rest
    }) => rest)(client);

    const filesForUpload = [...files?.clientImages, ...files?.clientPdf];

    let filesParsed = [];

    if (id) {
      if (filesForUpload?.length > 0) {
        const { data } = yield call(clientService.uploadDocuments, filesForUpload);

        if (data) {
          const filesData = data.map((file: any) => file?.document);

          filesParsed = filesData;
        }
      }

      yield call(clientService.editClient, {
        _id: id,
        ...clientParsed,
        registrationDate: new Date(),
        isEditable: false,
        documents: filesParsed
      });

      yield call(clientService.blockReason, {
        externalCodes: [clientParsed?.externalCode],
        blockReason: clientParsed?.blockReason?.externalCode ?? null
      });
    } else {
      if (filesForUpload.length > 0) {
        const { data } = yield call(clientService.uploadDocuments, filesForUpload);

        if (data) {
          const filesData = data.map((file: { document: string }) => file?.document);

          filesParsed = filesData;
        }
      }

      yield call(clientService.createClient, {
        ...clientParsed,
        documents: filesParsed
      });
    }

    yield call(history.push, `/register/client/`, {
      date: new Date().getTime()
    });
    yield put(clientActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('clientFormUpdatedMessage') : formatMessage('clientFormCreatedMessage')
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error?.response?.status === 422) {
        yield call(notification.warning, formatMessage('clientFormInvalidFields'));
      } else {
        yield put({ type: ISA_ERROR, error });
      }
    }
  } finally {
    yield put(clientActions.setLoadingSend(false));
  }
}

export function* deleteItem({ id, payload }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(clientActions.setLoadingSend(true));
      yield call(clientService.deleteClient, id, payload);

      yield put(clientActions.setLoadingSend(false));

      yield put(clientActions.getAllRequest());
      yield call(notification.success, formatMessage('commonLabelExcludeSuccess'));
    }
  } catch (error) {
    yield put(clientActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id, typeList }: any) {
  if (typeList) return yield call(history.push, `/register/client/edit/${id}/${typeList}`);
  yield call(history.push, `/register/client/edit/${id}`);
}

export function* saveRating({ payload, clientExternalCode, isModal }: any) {
  try {
    yield put(clientActions.setLoadingSend(true));
    const { externalCode: userExternalCode } = yield select((state: AppStateType) => state.auth.user);
    const { client } = yield select((state: AppStateType) => state.order.form.data);

    const parsedRating = {
      userExternalCode,
      clientExternalCode,
      rating: payload.rating,
      message: payload.comment ?? '',
      client: {
        activityExternalCode: payload?.activity?.externalCode,
        phoneNumber: payload?.telephone?.replace(/[&/\\#,+()$~%.'" ():*?<>{}-]/g, ''),
        cellphoneNumber: payload?.cellphone?.replace(/[&/\\#,+()$~%.'" ():*?<>{}-]/g, ''),
        email: payload?.email
      },
      nextVisitDate: payload?.nextVisitDate,
      hasBeenVisited: payload?.visited
    };

    yield call(clientService.saveRating, parsedRating);

    if (!isModal) {
      yield call(history.push, `/register/client/`, {
        date: new Date().getTime()
      });
    } else {
      yield put(orderActions.setClientRequest(client?._id));
    }

    yield delay(2000);

    yield call(notification.success, formatMessage('clientRatingFormCreatedMessage'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingSend(false));
  }
}

export function* saveImages({ files }: any) {
  try {
    yield put(clientActions.setLoadingSend(true));

    const { externalCode, _id } = yield select((state: AppStateType) => state.client.form.data);

    yield call(clientService.saveImagesClient, externalCode, files);

    yield put(clientActions.getByIdRequest(_id));

    yield call(notification.success, formatMessage('clientPhotoFormSaveImageMessage'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingSend(false));
  }
}

export function* deleteImage({ imageUrl }: any) {
  try {
    yield put(clientActions.setLoadingSend(true));

    const { externalCode, _id } = yield select((state: AppStateType) => state.client.form.data);

    yield call(clientService.deleteImage, imageUrl, externalCode);

    yield put(clientActions.getByIdRequest(_id));

    yield call(notification.success, formatMessage('clientPhotoFormDeleteImageMessage'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingSend(false));
  }
}

export function* saveChecklist({ payload, clientExternalCode, questionaryId }: any) {
  try {
    yield put(clientActions.setLoadingSend(true));

    const hasEmptyFields = Object.values(payload).some(obj => obj === '');

    if (hasEmptyFields) {
      yield call(notification.warning, formatMessage('clientChecklistFormEmptyFields'));
      return;
    }

    const parsedResponses = parsedArrayChecklist(payload);

    const parsedChecklist = {
      questionaryId,
      clientExternalCode,
      newResponses: parsedResponses
    };

    yield call(clientService.saveResponseChecklist, parsedChecklist);

    yield call(notification.success, formatMessage('clientChecklistFormSuccess'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_FILTER_REQUEST, getFilter),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(SAVE_IMAGES, saveImages),
  takeLatest(DELETE_IMAGE, deleteImage),
  takeLatest(SAVE_RATING_REQUEST, saveRating),
  takeLatest(SAVE_CHECKLIST_REQUEST, saveChecklist)
]);
